import { getChapterContract } from './chapterContracts'
import { assessBriefReadiness } from './briefReadiness'
import type { ThesisBrief, ChapterContract } from './types'
import type { ThesisGenerateAction } from '@/lib/thesisGenerateExtras'

const ACADEMIC_BASE = `你是 MBA 学位论文写作助手，协助学生完成「问题—框架—证据—启示」的论证，不是代写枪手，也不是小说作者。

硬性规则：
1. 使用中文学术书面语；主动语态优先；避免口语、鸡汤与空泛口号。
2. 一切论述必须服从开题卡中的研究问题、理论镜头、构念口径与研究边界（scopeOut）。
3. 不得编造数据、访谈原话、统计显著结果或未提供的文献细节；不确定处明确标注「待学生核实/补证」。
4. 严格遵守当前章契约：只写本章职责范围内的内容，不抢写后章、不回退重写已锁定的开题定义（若需改定义，先提示修订开题卡）。
5. 引用格式可用（作者，年份）占位，提醒学生补全正式文献。
6. 禁止使用小说技法口吻（「秀不说」、对话驱动情节、伏笔等）。`

function fmtList(items: string[], bullet = '-') {
  return items
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => `${bullet} ${s}`)
    .join('\n')
}

function briefBlock(brief: ThesisBrief): string {
  const constructs = brief.keyConstructs
    .filter((c) => c.name.trim())
    .map((c) => {
      const op = c.operationalization.trim() ? `；操作化：${c.operationalization.trim()}` : ''
      return `- ${c.name.trim()}：${c.definition.trim() || '（定义待补）'}${op}`
    })
    .join('\n')

  return `【开题卡 SSOT｜全文论证唯一真相源】
实践背景：${brief.practiceBackground || '（待补）'}
管理痛点：${brief.problemStatement || '（待补）'}
研究目的：${brief.researchPurpose || '（待补）'}
研究问题：
${fmtList(brief.researchQuestions) || '- （待补）'}
理论视角：${brief.theoreticalLens || '（待补）'}
关键构念：
${constructs || '- （待补）'}
假设/命题：
${fmtList(brief.hypothesesOrPropositions) || '- （探索性/无显式假设）'}
分析框架：${brief.analysisFramework || '（待补）'}
方法：${brief.methodType || '（待补）'}｜分析单位：${brief.unitOfAnalysis || '（待补）'}
案例/样本：${brief.caseOrSample || '（待补）'}
数据来源：${fmtList(brief.dataSources) || '- （待补）'}
分析计划：${brief.analysisPlan || '（待补）'}
研究范围内：${brief.scopeIn || '（待补）'}
明确不写（防跑偏）：${brief.scopeOut || '（待补）'}
预期贡献：
${fmtList(brief.expectedContributions) || '- （待补）'}`
}

function contractBlock(contract: ChapterContract): string {
  return `【本章契约｜第${contract.chapterIndex}章 ${contract.title}】
论证角色：${contract.roleInArgument}
必须回答：
${fmtList(contract.mustAnswer)}
必备结构：
${fmtList(contract.mustInclude)}
禁止：
${fmtList(contract.forbid)}
门控通过条件：
${fmtList(contract.exitCriteria)}
建议小节：${contract.suggestedSections.join('、')}`
}

function priorSummariesBlock(brief: ThesisBrief, chapterIndex: number): string {
  const lines: string[] = []
  for (let i = 1; i < chapterIndex; i++) {
    const g = brief.chapterGates[String(i)]
    if (!g) continue
    const sum = g.summary.trim()
    const st = g.status
    lines.push(
      `- 第${i}章 [${st}] ${sum || '（尚无通过门控的摘要；生成时勿臆造前章结论）'}`,
    )
  }
  if (!lines.length) return '【前章摘要】无（当前为第1章或前章未摘要）'
  return `【前章已锁定摘要｜后文必须与之连贯，不得推翻未提示修订的结论】\n${lines.join('\n')}`
}

const ACTION_HINT: Record<ThesisGenerateAction, string> = {
  continue: '按本章契约与开题卡续写正文，保持学术论证节奏。',
  outline: '在本章契约范围内扩写/细化小节标题与要点，勿写成后章内容。',
  expand: '将提纲或短段扩展为完整学术段落，证据处标注待核实。',
  polish: '润色为学术书面语，不改变论点与证据口径。',
  review:
    '对照开题卡与本章契约做编审：列出对齐处、跑偏处、缺证处，并给出可执行修改清单。不要重写全文。',
}

export function buildThesisSystemPrompt(args: {
  brief: ThesisBrief
  chapterIndex: number | null
  action: ThesisGenerateAction
  note?: string
}): string {
  const { brief, chapterIndex, action, note } = args
  const readiness = assessBriefReadiness(brief)
  const parts = [ACADEMIC_BASE, briefBlock(brief)]

  if (!readiness.ready) {
    parts.push(
      `【开题卡未就绪｜完备度 ${readiness.score}%】缺口：${readiness.gaps.map((g) => g.label).join('、')}。优先补开题卡；若仍生成，只允许帮助完善第1章问题表述或开题相关文字，并明确提示缺口。`,
    )
  }

  if (chapterIndex != null) {
    const contract = getChapterContract(chapterIndex)
    if (contract) {
      parts.push(contractBlock(contract))
      parts.push(priorSummariesBlock(brief, chapterIndex))
    }
  } else {
    parts.push('【章节定位】未能识别当前章序号；请按用户所选目录小节写，并主动询问是否对应第几章。')
  }

  parts.push(`【本次动作】${ACTION_HINT[action] || ACTION_HINT.continue}`)
  if (note?.trim()) parts.push(`【学生补充偏好】${note.trim()}`)

  return parts.join('\n\n')
}

/** 门控编审专用 system（结构化输出约定） */
export function buildGateReviewSystemPrompt(brief: ThesisBrief, chapterIndex: number): string {
  const contract = getChapterContract(chapterIndex)
  return `${ACADEMIC_BASE}

你正在做「章后门控编审」，不是续写正文。
对照开题卡与本章契约，评估草稿是否跑偏。

${briefBlock(brief)}

${contract ? contractBlock(contract) : ''}

请严格输出如下 JSON（不要 Markdown 围栏）：
{
  "passed": boolean,
  "alignScore": number,
  "findings": string[],
  "revisions": string[],
  "summaryIfPass": string
}
规则：
- alignScore 0–100：与 RQ/框架/本章职责的对齐程度
- passed=true 仅当基本满足 exitCriteria 且无明显 forbid 违规
- summaryIfPass：若通过，用 120–200 字写本章论证摘要，供后章注入；不通过则空字符串
- findings 写「已对齐/跑偏/缺证」观察；revisions 写可执行修改项`
}
